import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    Chip,
} from '@mui/material';
import useRootHook from '../../hooks/useRoot';
import stats from '../../models/stats';
import moment from 'moment';

/** Колонки сортировки (ключ = id колонки) */
const SORT = {
    paysystem: 'paysystem',
    paymethod: 'paymethod',
    maxDay: 'maxDay',
    dayTotal: 'dayTotal',
    dayRemain: 'dayRemain',
    maxWeek: 'maxWeek',
    weekTotal: 'weekTotal',
    weekRemain: 'weekRemain',
    status: 'status',
};

function rowPaysystem(r) {
    return String(r['paymethod.paysystem.name'] || r['paymethod.paysystem.key'] || '').trim() || '-';
}

function rowPaymethodSortKey(r) {
    const base = String(r['paymethod.name'] || `#${r.paymethod_id}`);
    const tag = r['paymethod.stripe_tag'] ? ` ${String(r['paymethod.stripe_tag']).trim()}` : '';
    return `${base}${tag}`;
}

function rowMaxDay(r, subunit) {
    return parseFloat(r.max_day_total || 0) / subunit;
}

/** null = нет дневного лимита (как «—» в UI) */
function rowMaxDaySort(r, subunit) {
    const v = rowMaxDay(r, subunit);
    return v > 0 ? v : null;
}

function rowMaxWeekSort(r, subunit) {
    const v = rowMaxWeek(r, subunit);
    return v > 0 ? v : null;
}

const NUMERIC_SORT_COLUMNS = new Set([
    SORT.maxDay,
    SORT.dayTotal,
    SORT.dayRemain,
    SORT.maxWeek,
    SORT.weekTotal,
    SORT.weekRemain,
    SORT.status,
]);

function rowDayTotal(r, subunit) {
    return parseFloat(r.day_total || 0) / subunit;
}

/** Чистый дневной остаток: лимит − зашло сегодня */
function rowDayRemainingRaw(r, subunit) {
    const maxD = rowMaxDay(r, subunit);
    if (maxD <= 0) return null;
    return Math.max(0, maxD - rowDayTotal(r, subunit));
}

/** Эффективный остаток с учётом недели (как в логике оплаты) */
function rowDayRemainEffective(r, subunit) {
    const raw = rowDayRemainingRaw(r, subunit);
    if (raw == null) return null;
    const weekRem = rowWeekRemain(r, subunit);
    if (weekRem == null) return raw;
    return Math.min(raw, weekRem);
}

function rowMaxWeek(r, subunit) {
    return parseFloat(r.max_week_total || 0) / subunit;
}

function rowWeekTotal(r, subunit) {
    return parseFloat(r.week_total || 0) / subunit;
}

/** null = нет недельного лимита (как «—» в UI) */
function rowWeekRemain(r, subunit) {
    const maxW = parseFloat(r.max_week_total || 0);
    if (maxW <= 0 || r.week_remaining == null) return null;
    return parseFloat(r.week_remaining) / subunit;
}

/**
 * Статус по худшему из дневного и недельного лимита (как в логике оплаты: неделя может «закрыть» день).
 * rank: 0 = No limit, 1 = OK, 2 = Warning, 3 = Exceeded
 */
function combinedLimitStatus(r, subunit) {
    const maxD = rowMaxDay(r, subunit);
    const dayT = rowDayTotal(r, subunit);
    const maxW = rowMaxWeek(r, subunit);
    const weekT = rowWeekTotal(r, subunit);
    const hasDay = maxD > 0;
    const hasWeek = maxW > 0;

    if (!hasDay && !hasWeek) {
        return { rank: 0, label: 'No limit', color: 'default' };
    }

    const dayPct = hasDay ? (dayT / maxD) * 100 : -1;
    const weekPct = hasWeek ? (weekT / maxW) * 100 : -1;

    const dayExc = hasDay && dayPct >= 100;
    const weekExc = hasWeek && weekPct >= 100;
    if (dayExc || weekExc) {
        return { rank: 3, label: 'Exceeded', color: 'error' };
    }

    const dayWarn = hasDay && dayPct >= 80;
    const weekWarn = hasWeek && weekPct >= 80;
    if (dayWarn || weekWarn) {
        return { rank: 2, label: 'Warning', color: 'warning' };
    }

    return { rank: 1, label: 'OK', color: 'success' };
}

function rowStatusRank(r, subunit) {
    return combinedLimitStatus(r, subunit).rank;
}

function compareNullableNumber(ka, kb, dir, tie) {
    const missA = ka == null;
    const missB = kb == null;
    if (missA && missB) return tie();
    if (missA) return 1;
    if (missB) return -1;
    const cmp = ka - kb;
    if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
    return tie();
}

function rowStripeTagKey(r) {
    return String(r['paymethod.stripe_tag'] || '').trim();
}

/** Сводка по stripe_tag: лимит / зашло / остаток (день и неделя) */
function buildTagGroups(rows, subunit) {
    const groups = new Map();

    for (const r of rows) {
        const tag = rowStripeTagKey(r);
        const key = tag || '__no_tag__';
        if (!groups.has(key)) {
            groups.set(key, {
                label: tag || 'Без группы',
                count: 0,
                maxDay: 0,
                dayTotal: 0,
                hasDayLimit: false,
                maxWeek: 0,
                weekTotal: 0,
                hasWeekLimit: false,
            });
        }
        const g = groups.get(key);
        g.count += 1;
        g.dayTotal += rowDayTotal(r, subunit);
        g.weekTotal += rowWeekTotal(r, subunit);

        const maxD = rowMaxDay(r, subunit);
        if (maxD > 0) {
            g.hasDayLimit = true;
            g.maxDay += maxD;
        }

        const maxW = rowMaxWeek(r, subunit);
        if (maxW > 0) {
            g.hasWeekLimit = true;
            g.maxWeek += maxW;
        }
    }

    for (const g of groups.values()) {
        g.dayRemainingRaw = g.hasDayLimit ? Math.max(0, g.maxDay - g.dayTotal) : null;
        g.weekRemainingRaw = g.hasWeekLimit ? Math.max(0, g.maxWeek - g.weekTotal) : null;
        g.effectiveRemaining =
            g.dayRemainingRaw != null && g.weekRemainingRaw != null
                ? Math.min(g.dayRemainingRaw, g.weekRemainingRaw)
                : g.dayRemainingRaw ?? g.weekRemainingRaw;
    }

    return Array.from(groups.values()).sort((a, b) => {
        if (a.label === 'Без группы') return 1;
        if (b.label === 'Без группы') return -1;
        return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });
}

function groupLimitStatus(g, subunit) {
    return combinedLimitStatus({
        max_day_total: g.hasDayLimit ? g.maxDay * subunit : 0,
        day_total: g.dayTotal * subunit,
        remaining: (g.effectiveRemaining ?? 0) * subunit,
        max_week_total: g.hasWeekLimit ? g.maxWeek * subunit : 0,
        week_total: g.weekTotal * subunit,
        week_remaining: g.weekRemainingRaw != null ? g.weekRemainingRaw * subunit : null,
    }, subunit);
}

function formatMoney(symbol, value, hasLimit = true) {
    if (!hasLimit) return '—';
    return `${symbol}${value.toFixed(2)}`;
}

export default function PaymethodDailyLimitsTable({filters}) {
    const {setShowLoader, defaultCurrency} = useRootHook();
    const [data, setData] = useState(null);
    const [sort, setSort] = useState({ column: null, direction: null });

    const todayFilters = useMemo(() => {
        const today = moment.utc().startOf('day');
        return {
            ...filters,
            from: today.toISOString(),
            to: today.endOf('day').toISOString(),
        };
    }, [filters]);

    const reqKey = useMemo(() => JSON.stringify({
        type: 'paymethodDailyLimits',
        from: todayFilters.from,
        to: todayFilters.to,
        paymethod_id: todayFilters.paymethod_id || null,
        project_id: todayFilters.project_id || null,
    }), [todayFilters.from, todayFilters.to, todayFilters.paymethod_id, todayFilters.project_id]);

    useEffect(() => {
        setShowLoader(true);
        stats.getStats({type: 'paymethodDailyLimits', ...todayFilters})
            .then((resp) => {
                setShowLoader(false);
                setData(resp || {items: []});
            })
            .catch(() => setShowLoader(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reqKey]);

    const rows = data?.items || [];
    const subunit = data?.subunit || defaultCurrency?.subunit || 1;
    const symbol = defaultCurrency?.symbol || '';

    const tagGroups = useMemo(() => buildTagGroups(rows, subunit), [rows, subunit]);
    const taggedGroups = useMemo(
        () => tagGroups.filter((g) => g.label !== 'Без группы'),
        [tagGroups]
    );

    const cycleSort = (column) => {
        setSort((prev) => {
            if (prev.column !== column) {
                return {
                    column,
                    direction: NUMERIC_SORT_COLUMNS.has(column) ? 'desc' : 'asc',
                };
            }
            return {
                column,
                direction: prev.direction === 'asc' ? 'desc' : 'asc',
            };
        });
    };

    const sortedRows = useMemo(() => {
        if (!sort.column || !sort.direction || !rows.length) return rows;
        const copy = [...rows];
        const tie = (a, b) => rowPaymethodSortKey(a).localeCompare(rowPaymethodSortKey(b));

        copy.sort((a, b) => {
            const dir = sort.direction;
            switch (sort.column) {
                case SORT.paysystem: {
                    const cmp = rowPaysystem(a).localeCompare(rowPaysystem(b), undefined, { sensitivity: 'base' });
                    if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
                    return tie(a, b);
                }
                case SORT.paymethod: {
                    const cmp = rowPaymethodSortKey(a).localeCompare(rowPaymethodSortKey(b), undefined, { sensitivity: 'base' });
                    return dir === 'asc' ? cmp : -cmp;
                }
                case SORT.maxDay:
                    return compareNullableNumber(
                        rowMaxDaySort(a, subunit),
                        rowMaxDaySort(b, subunit),
                        dir,
                        () => tie(a, b)
                    );
                case SORT.dayTotal: {
                    const cmp = rowDayTotal(a, subunit) - rowDayTotal(b, subunit);
                    if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
                    return tie(a, b);
                }
                case SORT.dayRemain:
                    return compareNullableNumber(
                        rowDayRemainingRaw(a, subunit),
                        rowDayRemainingRaw(b, subunit),
                        dir,
                        () => tie(a, b)
                    );
                case SORT.maxWeek:
                    return compareNullableNumber(
                        rowMaxWeekSort(a, subunit),
                        rowMaxWeekSort(b, subunit),
                        dir,
                        () => tie(a, b)
                    );
                case SORT.weekTotal: {
                    const hasWeekA = rowMaxWeekSort(a, subunit) != null;
                    const hasWeekB = rowMaxWeekSort(b, subunit) != null;
                    if (!hasWeekA && !hasWeekB) return tie(a, b);
                    if (!hasWeekA) return 1;
                    if (!hasWeekB) return -1;
                    const cmp = rowWeekTotal(a, subunit) - rowWeekTotal(b, subunit);
                    if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
                    return tie(a, b);
                }
                case SORT.weekRemain:
                    return compareNullableNumber(
                        rowWeekRemain(a, subunit),
                        rowWeekRemain(b, subunit),
                        dir,
                        () => tie(a, b)
                    );
                case SORT.status: {
                    const ra = rowStatusRank(a, subunit);
                    const rb = rowStatusRank(b, subunit);
                    const cmp = ra - rb;
                    if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
                    return tie(a, b);
                }
                default:
                    return 0;
            }
        });
        return copy;
    }, [rows, sort.column, sort.direction, subunit]);

    const sortLabelProps = (column) => ({
        active: sort.column === column,
        direction: sort.column === column && sort.direction ? sort.direction : 'asc',
        hideSortIcon: sort.column !== column,
        onClick: () => cycleSort(column),
    });

    const headCellSort = (column) => (sort.column === column ? sort.direction : false);

    const sortLabelSx = { width: '100%', justifyContent: 'flex-end' };
    const sortLabelSxLeft = { width: '100%', justifyContent: 'flex-start' };

    return (
        <React.Fragment>
            {taggedGroups.length > 0 && (
                <React.Fragment>
                    <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
                        Сводка по группам (Tag)
                    </Typography>
                    <Table size="small" sx={{ mb: 3 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Группа</TableCell>
                                <TableCell align="right">Методов</TableCell>
                                <TableCell align="right">Дневной лимит</TableCell>
                                <TableCell align="right">За сегодня</TableCell>
                                <TableCell align="right">Остаток дня</TableCell>
                                <TableCell align="right">Недельный лимит</TableCell>
                                <TableCell align="right">За 7 дней</TableCell>
                                <TableCell align="right">Остаток недели</TableCell>
                                <TableCell align="center">Статус</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {taggedGroups.map((g) => {
                                const status = groupLimitStatus(g, subunit);
                                return (
                                    <TableRow key={`tag-group-${g.label}`} sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell sx={{ fontWeight: 700 }}>{g.label}</TableCell>
                                        <TableCell align="right">{g.count}</TableCell>
                                        <TableCell align="right">
                                            {formatMoney(symbol, g.maxDay, g.hasDayLimit)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {symbol}{g.dayTotal.toFixed(2)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {g.dayRemainingRaw != null
                                                ? `${symbol}${g.dayRemainingRaw.toFixed(2)}`
                                                : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatMoney(symbol, g.maxWeek, g.hasWeekLimit)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {g.hasWeekLimit ? `${symbol}${g.weekTotal.toFixed(2)}` : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {g.weekRemainingRaw != null
                                                ? `${symbol}${g.weekRemainingRaw.toFixed(2)}`
                                                : '—'}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip size="small" label={status.label} color={status.color} />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </React.Fragment>
            )}
            <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
                Лимиты по платежным системам (сегодня и 7 дней)
            </Typography>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sortDirection={headCellSort(SORT.paysystem)}>
                            <TableSortLabel {...sortLabelProps(SORT.paysystem)} sx={sortLabelSxLeft}>
                                Платежная система
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={headCellSort(SORT.paymethod)}>
                            <TableSortLabel {...sortLabelProps(SORT.paymethod)} sx={sortLabelSxLeft}>
                                Метод оплаты
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={headCellSort(SORT.maxDay)}>
                            <TableSortLabel {...sortLabelProps(SORT.maxDay)} sx={sortLabelSx}>
                                Дневной лимит
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={headCellSort(SORT.dayTotal)}>
                            <TableSortLabel {...sortLabelProps(SORT.dayTotal)} sx={sortLabelSx}>
                                За сегодня
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={headCellSort(SORT.dayRemain)}>
                            <TableSortLabel {...sortLabelProps(SORT.dayRemain)} sx={sortLabelSx}>
                                Остаток дня
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={headCellSort(SORT.maxWeek)}>
                            <TableSortLabel {...sortLabelProps(SORT.maxWeek)} sx={sortLabelSx}>
                                Недельный лимит
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={headCellSort(SORT.weekTotal)}>
                            <TableSortLabel {...sortLabelProps(SORT.weekTotal)} sx={sortLabelSx}>
                                За 7 дней
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={headCellSort(SORT.weekRemain)}>
                            <TableSortLabel {...sortLabelProps(SORT.weekRemain)} sx={sortLabelSx}>
                                Остаток недели
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="center" sortDirection={headCellSort(SORT.status)}>
                            <TableSortLabel {...sortLabelProps(SORT.status)} sx={{ width: '100%', justifyContent: 'center' }}>
                                Статус
                            </TableSortLabel>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedRows.map((r) => {
                        const paysystemName = r['paymethod.paysystem.name'] || r['paymethod.paysystem.key']?.toUpperCase() || '-';
                        const paymethodName = r['paymethod.name'] || `#${r.paymethod_id}`;
                        const stripeTag = r['paymethod.stripe_tag'] ? ` · ${r['paymethod.stripe_tag']}` : '';
                        const maxDayTotal = parseFloat(r.max_day_total || 0) / subunit;
                        const dayTotal = parseFloat(r.day_total || 0) / subunit;
                        const dayRemainingRaw = rowDayRemainingRaw(r, subunit);
                        const dayRemainingEffective = rowDayRemainEffective(r, subunit);
                        const maxWeekTotal = parseFloat(r.max_week_total || 0) / subunit;
                        const weekTotal = parseFloat(r.week_total || 0) / subunit;
                        const weekRem = rowWeekRemain(r, subunit);
                        const weekCapsDay =
                            dayRemainingRaw != null
                            && dayRemainingEffective != null
                            && dayRemainingEffective < dayRemainingRaw - 0.005;
                        const status = combinedLimitStatus(r, subunit);

                        return (
                            <TableRow key={`daily-limit-${r.paymethod_id}`}>
                                <TableCell>{paysystemName}</TableCell>
                                <TableCell>{paymethodName}{stripeTag}</TableCell>
                                <TableCell align="right">
                                    {maxDayTotal > 0 ? `${symbol}${maxDayTotal.toFixed(2)}` : '—'}
                                </TableCell>
                                <TableCell align="right">
                                    {symbol}{dayTotal.toFixed(2)}
                                </TableCell>
                                <TableCell align="right">
                                    {dayRemainingRaw != null ? (
                                        <React.Fragment>
                                            {symbol}{dayRemainingRaw.toFixed(2)}
                                            {weekCapsDay && (
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                    color="warning.main"
                                                    sx={{ display: 'block', lineHeight: 1.2 }}
                                                    title="Доступно с учётом недельного лимита"
                                                >
                                                    доступно {symbol}{dayRemainingEffective.toFixed(2)}
                                                </Typography>
                                            )}
                                        </React.Fragment>
                                    ) : '—'}
                                </TableCell>
                                <TableCell align="right">
                                    {maxWeekTotal > 0 ? `${symbol}${maxWeekTotal.toFixed(2)}` : '—'}
                                </TableCell>
                                <TableCell align="right">
                                    {maxWeekTotal > 0 ? `${symbol}${weekTotal.toFixed(2)}` : '—'}
                                </TableCell>
                                <TableCell align="right">
                                    {weekRem != null && maxWeekTotal > 0 ? `${symbol}${weekRem.toFixed(2)}` : '—'}
                                </TableCell>
                                <TableCell align="center">
                                    <Chip
                                        size="small"
                                        label={status.label}
                                        color={status.color}
                                    />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    {sortedRows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={9}>
                                <Typography variant="body2" color="text.secondary">
                                    Нет данных для отображения.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </React.Fragment>
    );
}
